/* eslint-disable class-methods-use-this */
import memoize from 'lodash/memoize';
import { store } from 'utils/store';
import './intercom.css';
import { questionnaireApi } from 'modules/questionnaire/api';
// eslint-disable-next-line import/no-cycle
import app from 'new-ui/app';
import { DOM, getIndicationIntercomTitle } from 'new-ui/functions';
import { SurveyTypes } from 'modules/survey/surveyEnums';
import { getSurvey } from 'modules/survey/api';
import { selectIsAnyPopupShown } from 'modules/popup/popup.selectors';
import { sortTreatmentByLatest } from 'new-ui/functions/sortTreatmentByLatest';
import {
  getIntercomMissingCriticalObject,
  getTopGroupsForIntercom, hideIntercomContainer,
  isEmpty,
  observeIsAppPopupShownStore,
  showIntercomContainer,
  watchIntercomPopups,
} from './functions';
import { INTERCOM_EVENTS } from './constants';
import { setIntercomValue } from './intercom.actions';

// https://developers.intercom.com/installing-intercom/web/methods

const getDrugs = memoize(() => questionnaireApi.getDrugs(), null);

class IntercomClient {
  // init value should be null - unknown
  isIntercomPopupInDOM = null;

  constructor() {
    const { intercomSettings } = window;
    this.settings = intercomSettings;
    this.appId = this.settings.app_id;
    this.isBooted = false;
    this.setup();
  }

  async getIntercomUser({ user, userTrials } = {}) {
    const trials = userTrials || app?.trials || [];
    const newTrials = trials?.filter((trial) => trial.isNew);
    const nearestNewTrial = newTrials.sort(
      (a, b) => (a.nearest_facility_distance >= b.nearest_facility_distance ? 1 : -1),
    )[0];
    const topGroups = getTopGroupsForIntercom(trials);
    const sponsored = (trials && trials.length) ? trials.filter((t) => t.isSponsored).length : 0;

    const { appConfiguration: { providerSpecific: providerSpecificConfiguration } } = store.getState();

    let intercomUser = {
      app_id: this.appId,
      user_id: user?.condition_profile?.user_wix_id || user?.personal?.user_wix_id,
      created_at: user?.personal?.registered_at,
      app_last_updated: app?.user?.personal?.profile_updated_at
        ? Math.floor(new Date(app.user.personal.profile_updated_at).getTime() / 1000) : null,
      email: user?.info?.email,
      name: user?.info?.family_name,
      user_type: user?.info?.user_type,
      phone: user?.info?.phone,
      has_location: !isEmpty(user?.info?.country),
      num_targeted_therapy: trials.filter((a) => a.groups.includes('Targeted therapy')),
      zendesk_engagement_status: user?.personal?.user_engagement_status,
      profile_updated_at: Math.floor(new Date(user?.personal?.profile_updated_at
        ? user.personal.profile_updated_at : new Date()).getTime() / 1000),
      condition: getIndicationIntercomTitle(user?.personal?.condition, user?.condition_profile?.type_of_ca),
      disease_status: user?.condition_profile?.disease_status,
      ecog: user?.condition_profile?.ecog || 0,
      ngs: user?.condition_profile?.ngs || 0,
      biomarkers: (user?.condition_profile?.biomarkers && user?.condition_profile?.biomarkers.length)
        ? user?.condition_profile?.biomarkers.length : 0,
      number_of_trials: trials.length,
      number_of_new_trials: newTrials.length,
      nearest_new_trial_name: nearestNewTrial && nearestNewTrial.title,
      nearest_new_trial_phase: nearestNewTrial && nearestNewTrial._phases.join(', '),
      nearest_new_trial_type: nearestNewTrial && nearestNewTrial.groups.join(', '),
      nearest_new_trial_location: nearestNewTrial && nearestNewTrial.closest_facility?.facility_name,
      provider: providerSpecificConfiguration?.code || 'Leal',
      customer_ref: user?.personal?.customerRef,
      Intervention_types: topGroups,
      continue_later: false,
      sponsored,
      file_types: null,
      last_document_date: null,
      last_upload_date: null,
    };
    const trialsForMissingCritical = userTrials || app?.trials || [];
    const missingCriticalObject = getIntercomMissingCriticalObject(user, trialsForMissingCritical);
    for (const x in missingCriticalObject) intercomUser[x] = missingCriticalObject[x];

    let uniqueAnswers = [];

    try {
      const surveyUserWixId = app.user.info.user_wix_id || intercomUser.user_id;
      const r = await getSurvey(SurveyTypes.treatmentExperience, surveyUserWixId);
      if (r.data.answers) {
        const answers = r.data.answers || [];
        uniqueAnswers = [...new Set(answers.map((b) => b.surveyKey).filter((b) => b !== 'general'))];
      }
    } catch { /* empty */ }
    const treatmentsLength = app?.user?.condition_profile?.treatments?.length
      || user?.condition_profile?.treatments?.length;
    const numTreatments = treatmentsLength || 0;
    let latestTreatment = '';

    if (numTreatments) {
      const drugs = await getDrugs();
      try {
        const [lastTreatment] = sortTreatmentByLatest(app?.user?.condition_profile?.treatments || []);
        latestTreatment = lastTreatment.drugs_received.map((drug) => {
          if (drugs.length) {
            const d = drugs.data.find((a) => a.code === drug);
            return d ? window.t(d.name) : drug;
          }
          return drug;
        }).sort()?.join(', ');
      } catch { /* empty */ }
    }

    const treatmentData = {
      num_treatment_lines: numTreatments,
      treatment_experience_num_reported: uniqueAnswers.length,
      treatment_experience_num_unanswered: numTreatments - uniqueAnswers.length,
      latest_treatment: latestTreatment,
    };

    intercomUser = Object.assign(intercomUser, treatmentData);

    app.__lastIntercomUser = { ...intercomUser };
    return intercomUser;
  }

  setup() {
    observeIsAppPopupShownStore((nextIsAppPopupShown) => {
      const { isIntercomPopupInDOM } = this;
      if (isIntercomPopupInDOM && !nextIsAppPopupShown) {
        showIntercomContainer();
      }
    });

    watchIntercomPopups((nextIsIntercomPopupInDOM) => {
      const { isIntercomPopupInDOM } = this;
      if (isIntercomPopupInDOM === nextIsIntercomPopupInDOM) {
        return;
      }
      this.isIntercomPopupInDOM = nextIsIntercomPopupInDOM;
      store.dispatch(setIntercomValue({ isIntercomPopupInDOM: nextIsIntercomPopupInDOM }));

      const isAppPopupShown = selectIsAnyPopupShown(store.getState());

      if (!isAppPopupShown && nextIsIntercomPopupInDOM) {
        showIntercomContainer();
      } else if (!nextIsIntercomPopupInDOM) {
        hideIntercomContainer();
      }
    });

    setInterval(() => {
      const borderlessIframe = document.querySelector('.intercom-borderless-frame');
      const chatIframe = document.querySelector('iframe[name="intercom-chat-frame"]');
      const crmLauncher = document.getElementById('launcher');
      const webWidget = document.getElementById('webWidget');
      app.crmVisibility = crmLauncher && getComputedStyle(crmLauncher)?.getPropertyValue('visibility') === 'visible';
      app.webWidgetVisibility = webWidget && getComputedStyle(webWidget)?.getPropertyValue('visibility') === 'visible';
      const messengerIframe = document.querySelector('.intercom-messenger-frame');
      if (messengerIframe) {
        const computed = window.getComputedStyle(messengerIframe);
        if (computed.pointerEvents === 'all') {
          this.createOverlay();
          if (computed.bottom === '0px') {
            const $topButton = document.querySelector('.intercom-top-button');
            if ($topButton) return;
            this.createTopButton();
          } else {
            this.removeTopButton();
          }
          return;
        }
        this.removeTopButton();
      }
      this.removeOverlay();
      if (borderlessIframe || chatIframe) {
        this.createOverlay();
      }
    }, 500);
  }

  async bootPublic() {
    if (!this.isBooted) {
      window.Intercom('boot', {
        app_id: this.appId,
      });
    }
    this.isBooted = true;
  }

  async boot(params = {}) {
    const { user = app?.user, data = {} } = params;

    if (!user) return;
    this.user = {
      ...await this.getIntercomUser({ user }),
      ...data,
    };

    const o = this.getStorageObject();

    if (o?.openOnBoot?.type === 'conversation') this.createOverlay();

    // only fires when the main Messenger opens
    // not when a conversation popup (“message” or “post”) is auto-shown.
    window.Intercom('onShow', this.onshow.bind(this));
    window.Intercom('onHide', this.onhide.bind(this));
    if (!this.isBooted) {
      window.Intercom('boot', this.user);
    } else {
      this.update();
    }
    this.isBooted = true;
  }

  async update({ user = app?.user, data = {}, userTrials } = {}) {
    if (!user) return;
    window.Intercom('update', {
      ...await this.getIntercomUser({ user, userTrials }),
      ...data,
    });
  }

  async updateUserAttributes(attributes, userWixId) {
    if (!userWixId) return;
    window.Intercom('update', {
      app_id: this.appId,
      user_id: userWixId,
      ...attributes,
    });
  }

  sendEvent(eventName, metadata = {}) {
    // console.log('sendEvent', eventName);
    window.Intercom('trackEvent', eventName, metadata);
  }

  shutdown() {
    window.Intercom('shutdown');
  }

  show() {
    window.Intercom('show');
  }

  hide() {
    window.Intercom('hide');
  }

  createOverlay() {
    this.removeOverlay();
    const $overlay = DOM('.intercom-overlay');
    document.body.appendChild($overlay);
  }

  removeOverlay() {
    const $overlay = document.querySelector('.intercom-overlay');
    if ($overlay) document.body.removeChild($overlay);
  }

  createTopButton() {
    const $topButton = DOM('.intercom-top-button', {
      onclick: () => {
        window.Intercom('hide');
      },
    });
    document.body.appendChild($topButton);
  }

  removeTopButton() {
    const $topButton = document.querySelector('.intercom-top-button');
    if ($topButton) document.body.removeChild($topButton);
  }

  onshow() {
    this.createOverlay();
  }

  onhide() {
    this.removeOverlay();
  }

  getStorageObject() {
    let o = null;
    for (let x = 0; x < localStorage.length; x += 1) {
      const n = localStorage.key(x);
      if (n.startsWith('intercom')) {
        o = JSON.parse(localStorage.getItem(n));
      }
    }
    return o;
  }
}

export const intercom = new IntercomClient();

export const intercomSignupEvent = () => {
  setTimeout(() => { intercom.sendEvent(INTERCOM_EVENTS.GENERAL.SIGNUP_15_SECONDS); }, 0);
  setTimeout(() => { intercom.sendEvent(INTERCOM_EVENTS.GENERAL.SIGNUP_30_SECONDS); }, 15000);
};

export { INTERCOM_EVENTS };
